import { Navigate, Route, createRoutesFromElements } from "react-router-dom";
import { isNative } from "./native";
import Layout, { type RouteHandle } from "./components/layout/Layout";
import RootLayout from "./components/layout/RootLayout";
import RequireAuth from "./components/layout/RequireAuth";
import NotFoundPage from "./screens/NotFoundPage";
import AppGuidePage from "./screens/AppGuidePage";
import Showcase from "./dev/Showcase";
import ActivityTestPage, { ActivityTestSubPage } from "./dev/ActivityTestPage";
import DebugMenuPage from "./pages/dev/DebugMenuPage";
import RequireDebugUnlock from "./components/layout/RequireDebugUnlock";

// Auth flow 라우트 (login/terms/nickname) 는 element=null (overlay only) 이라
// 컴포넌트 import 불필요. 실제 컴포넌트는 ActivityHost 의 AuthEntryComponent 에서 직접 import 함.
import TermsViewerPage from "./pages/auth/TermsViewerPage";
import PublicTermsPage from "./pages/legal/PublicTermsPage";
import AccountDeletionPage from "./pages/legal/AccountDeletionPage";
import OAuthCallbackPage from "./pages/auth/OAuthCallbackPage";
import MyPage from "./pages/me/MyPage";

// 메인 탭 (홈 / 검색 / 스캔 / 컬렉션 / 내 정보) — 기획확정안 §7
import HomePage from "./pages/home/HomePage";
import CollectionsPage from "./pages/collection/CollectionsPage";
import HoldingFormPage from "./pages/collection/HoldingFormPage";

// 카드/카탈로그
import CardDetailPage from "./pages/card/CardDetailPage";
import CardSharePage from "./pages/card/CardSharePage";
import CardImagePage from "./pages/card/CardImagePage";
import SearchPage from "./pages/card/SearchPage";
import SetDetailPage from "./pages/card/SetDetailPage";
import VariantSelectPage from "./pages/card/VariantSelectPage";

// 알림
import NotificationCenterPage from "./pages/notification/NotificationCenterPage";
import NotificationDetailPage from "./pages/notification/NotificationDetailPage";

// 마이 / 지원
import SupportPage from "./pages/support/SupportPage";
import ProfileEditPage from "./pages/me/ProfileEditPage";
import WithdrawPage from "./pages/me/WithdrawPage";

/**
 * Route handle 헬퍼 — Layout 의 RouteHandle 타입 그대로 통과시키며 readable 마크업.
 *   handle={h({ appBarVariant: 'detail', appBarTitle: '설정' })}
 */
const h = (config: RouteHandle): RouteHandle => config;

/**
 * detail variant + title 헬퍼.
 * 상세 페이지는 모두 모달성 — bottomNav 기본 false.
 */
const detail = (title: string, opts?: Partial<RouteHandle>): RouteHandle => ({
  appBarVariant: "detail",
  appBarTitle: title,
  bottomNav: false,
  ...opts,
});

/**
 * Route tree — main.tsx 가 createBrowserRouter 로 감싸 RouterProvider 에 넘김.
 * (createBrowserRouter = data router → useMatches/handle 사용 가능)
 */
export const routes = createRoutesFromElements(
  <Route element={<RootLayout />}>
      {/* === Auth flow 라우트 — useActivityHydration 이 auth activity 를 hydrate
            해서 ActivityHost 가 overlay 로 렌더하므로 underlying 라우트 element 는
            null. === */}
      <Route path="/login" element={null} />
      <Route path="/oauth/google/callback" element={<OAuthCallbackPage provider="google" />} />
      <Route path="/oauth/apple/callback" element={<OAuthCallbackPage provider="apple" />} />

      {/* === Dev (영구 보존) === */}
      <Route path="/dev/showcase" element={<Showcase />} />
      <Route path="/dev/activity-test" element={<ActivityTestPage />} />
      <Route path="/dev/activity-test/sub-a" element={<ActivityTestSubPage name="a" />} />
      <Route path="/dev/activity-test/sub-b" element={<ActivityTestSubPage name="b" />} />
      <Route path="/dev/activity-test/sub-c" element={<ActivityTestSubPage name="c" />} />
      <Route path="/dev/activity-test/sub-d" element={<ActivityTestSubPage name="d" />} />
      <Route path="/dev/activity-test/sub-:name-deeper" element={<ActivityTestSubPage name="deeper" />} />
      <Route
        path="/dev/debug-menu"
        element={
          <RequireDebugUnlock>
            <DebugMenuPage />
          </RequireDebugUnlock>
        }
      />

      {/* ============================================================
       * 메인 라우트 — 단일 Layout 인스턴스가 모두 감쌈.
       * ============================================================ */}
      <Route element={<Layout />}>
        {/* === 메인 탭 (기획확정안 §7: 홈·검색·스캔·컬렉션·내정보, 스캔 중앙) === */}
        <Route path="/" element={<HomePage />} handle={h({ appBarVariant: "home" })} />
        {/* 스캔은 네이티브 전용 (카메라 엔진이 웹에 없음) — 웹 직접 진입은 홈으로.
            네이티브에서는 activity(ActivityHost SUB_ACTIVITY_RESOLVERS.scan)가 렌더하므로
            라우트 element 는 비워 둔다 (Layout 이 URL 을 흡수해 배경 탭을 유지). */}
        <Route path="/scan" element={isNative() ? null : <Navigate to="/" replace />} handle={h({ appBar: false })} />
        <Route path="/collections" element={<CollectionsPage />} handle={h({ appBarVariant: "home", appBarTitle: "nav.route.collection" })} />
        {/* §6.2 저장 폼 — 등록은 스캔 결과에서, 수정은 컬렉션 목록에서 들어온다.
            'new' 가 :holdingSerialNo 에 먹히지 않도록 구체 경로를 먼저 둔다.
            (design 의 컬렉션 묶음 상세 SC-014·수동 추가 SC-016 은 BE CRUD 미비로 보류) */}
        <Route path="/collections/holdings/new" element={<RequireAuth><HoldingFormPage /></RequireAuth>} handle={detail("nav.route.addHolding")} />
        <Route path="/collections/holdings/:holdingSerialNo" element={<RequireAuth><HoldingFormPage /></RequireAuth>} handle={detail("nav.route.editHolding")} />

        {/* === 카드/카탈로그 === */}
        {/* §3.3 세부 버전 선택 — 기본 카드 식별 직후. 버전 1개면 자동으로 상세로 replace */}
        <Route path="/cards/:cardSerialNo/variant" element={<VariantSelectPage />} handle={detail("nav.route.variantSelect")} />
        {/* 파라미터는 **cardSerialNo**(수집서버 전역 ID)다 — BE 상세가 그 키를 받고
            스캔 매처가 돌려주는 값과도 같다. 더미 시절엔 문자열 uid 였다. */}
        <Route path="/cards/:cardSerialNo" element={<CardDetailPage />} handle={detail("nav.route.cardDetail")} />
        {/* SC-020 카드 공유 이미지 (FN-030) — 카드 상세에서 내려온다 */}
        <Route path="/cards/:cardSerialNo/share" element={<CardSharePage />} handle={detail("nav.route.share")} />
        {/* SC-008 카드 원본 이미지 (FN-020) — 카드 상세에서 이미지를 눌러 내려온다 */}
        <Route path="/cards/:cardSerialNo/image" element={<CardImagePage />} handle={detail("nav.route.cardImage")} />
        <Route path="/search" element={<SearchPage />} handle={h({ appBarVariant: "home", appBarTitle: "nav.route.search" })} />
        <Route path="/sets/:cardsetSerialNo" element={<SetDetailPage />} handle={detail("nav.route.set")} />
        {/* SC-019a(홈 §2 더보기)는 라우트가 아니라 홈 안의 바텀시트다 —
            components/home/TrendingAllSheet */}

        {/* === 탭에서 내려온 템플릿 화면 (알림/마이 — 탭 아님, 상세 스타일로 유지) === */}
        <Route path="/notifications" element={<RequireAuth><NotificationCenterPage /></RequireAuth>} handle={h({ appBarVariant: "home", appBarTitle: "nav.route.notifications", bottomNav: false })} />
        <Route path="/me" element={<RequireAuth><MyPage /></RequireAuth>} handle={h({ appBarVariant: "myPage", appBarTitle: "nav.route.me" })} />
        <Route path="/terms/view" element={<TermsViewerPage />} handle={detail("nav.route.terms", { appBar: false })} />

        {/* === 알림 상세 === */}
        <Route path="/notifications/:id" element={<RequireAuth><NotificationDetailPage /></RequireAuth>} handle={detail("nav.route.notificationDetail")} />

        {/* === 프로필 수정 (sticky CTA → bottomNav 비활성) === */}
        <Route path="/me/profile" element={<RequireAuth><ProfileEditPage /></RequireAuth>} handle={detail("nav.route.editProfile")} />

        {/* === 마이 자식 페이지들 — 모두 detail variant === */}
        {/* 설정 화면은 2026-08-24 에 없앴다 (design 2026-08-20) — 내용은 전부 내
            정보로 합쳤다. 라우트는 남긴다: 예전 딥링크·푸시·디버그 진입이 이 주소를 든다. */}
        <Route path="/me/settings" element={<Navigate to="/me" replace />} handle={detail("nav.route.settings")} />
        <Route path="/me/withdraw" element={<RequireAuth><WithdrawPage /></RequireAuth>} handle={detail("nav.route.withdraw")} />

        {/* === 고객센터 === */}
        <Route path="/support" element={<SupportPage />} handle={detail("nav.route.support")} />
        <Route path="/support/faq" element={<Navigate to="/support" replace />} handle={detail("nav.route.support")} />
      </Route>

      {/* === 공개 약관 — 외부(구글 OAuth 동의화면·앱스토어 심사)에서 직접 여는 URL ===
          webGateRoutes 에만 있으면 dev 서버(게이트 우회)와 네이티브에서 404 가 난다.
          외부에 등록하는 주소이므로 어느 라우트셋에서도 항상 열려야 한다. */}
      <Route path="/legal/privacy" element={<PublicTermsPage type="privacy" />} />
      <Route path="/legal/terms" element={<PublicTermsPage type="service" />} />
      <Route path="/legal/account-deletion" element={<AccountDeletionPage />} />

      {/* === Catch-all === */}
      <Route path="*" element={<NotFoundPage />} />
  </Route>,
);

/**
 * 앱 안내 라우트 셋 — 공개 약관만 살리고 나머지는 앱 소개 페이지.
 *
 * main.tsx 가 안내 호스트(VITE_LANDING_HOST)이거나
 * "웹 환경(!isNative) && 웹 비공개(VITE_WEB_PUBLIC !== 'true')" 일 때 이 셋을 사용.
 * 네이티브 앱 / OTA 는 항상 isNative()===true → 전체 routes 사용 (영향 없음).
 */
export const webGateRoutes = createRoutesFromElements(
  <Route element={<RootLayout />}>
    {/* 심사·외부 링크용 공개 약관 — 웹 비공개 기간에도 예외로 노출. */}
    <Route path="/legal/privacy" element={<PublicTermsPage type="privacy" />} />
    <Route path="/legal/terms" element={<PublicTermsPage type="service" />} />
    <Route path="/legal/account-deletion" element={<AccountDeletionPage />} />
    <Route path="*" element={<AppGuidePage />} />
  </Route>,
);
